import { t } from "@lingui/macro";
import { CircleNotch, Sparkle } from "@phosphor-icons/react";
import { type ResumeDraft, seedTypicalWork } from "@reactive-resume/schema";
import { Button } from "@reactive-resume/ui";
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";

import { useTakingLonger } from "@/client/hooks/use-taking-longer";
import { trackProductEvent, withOnboardingTiming } from "@/client/libs/analytics";
import { reportAiOutcome } from "@/client/services/ai";

import { EnrichToggle } from "./enrich-toggle";
import { extractDraftFromNotes } from "./extract-draft";
import { NoteField, type NoteFieldHandle } from "./note-field";
import { parsePlainTextToDraft } from "./parse-plain-text";
import {
  type ChipKey,
  blockingPlaceholdersIn,
  exampleNote,
  FACT_STEM,
  findHintTarget,
  joinFact,
  notesCoverage,
  placeholdersIn,
  PromptHints,
} from "./prompt-hints";

type Props = {
  name?: string;
  email?: string;
  firstName?: string;
  enrich: boolean;
  loading?: boolean;
  error?: string | null;
  writing?: boolean;
  onEnrichChange: (value: boolean) => void;
  onStarted?: () => void;
  onImport?: () => void;
  onSkip?: () => void;
  onDraftChange: (draft: ResumeDraft, reading: boolean) => void;
  onWritingChange?: (writing: boolean) => void;
  onSubmit: (text: string, previewDraft?: ResumeDraft) => void | Promise<void>;
};

const MAX_CHARS = 12_000;
const AI_MIN_CHARS = 40;
const AI_DEBOUNCE_MS = 450;

export const Composer = ({
  name,
  email,
  firstName,
  enrich,
  loading,
  error,
  writing,
  onEnrichChange,
  onStarted,
  onImport,
  onSkip,
  onDraftChange,
  onWritingChange,
  onSubmit,
}: Props) => {
  const [text, setText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const takingLonger = useTakingLonger(Boolean(writing));
  const [draft, setDraft] = useState<ResumeDraft>({});
  const fieldRef = useRef<NoteFieldHandle>(null);
  const requestId = useRef(0);
  const latestDraft = useRef<ResumeDraft>({});
  const lastAiText = useRef("");
  const lastEnrichedDraft = useRef<ResumeDraft | null>(null);
  const lastCallId = useRef<string | undefined>(undefined);
  const prevText = useRef(text);
  const inflight = useRef<Promise<ResumeDraft | null> | null>(null);
  const inflightText = useRef("");
  const startedRef = useRef(false);

  const pendingSelect = useRef<{ start: number; end: number } | null>(null);

  const isMac =
    typeof navigator !== "undefined" &&
    /Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent);

  const publish = useCallback(
    (next: ResumeDraft, reading: boolean) => {
      latestDraft.current = next;
      setDraft(next);
      onDraftChange(next, reading);
    },
    [onDraftChange],
  );

  const applyLocalPreview = useCallback(
    (nextText: string, nextEnrich: boolean) => {
      const parsed = parsePlainTextToDraft(nextText, { name, email });
      const trimmed = nextText.trim();
      if (!nextEnrich) {
        publish(parsed, trimmed.length > 0);
        return;
      }
      if (lastEnrichedDraft.current && lastAiText.current === trimmed) {
        publish(lastEnrichedDraft.current, false);
        return;
      }
      publish(seedTypicalWork(parsed), trimmed.length > 0);
    },
    [name, email, publish],
  );

  useEffect(() => {
    const textChanged = prevText.current !== text;
    prevText.current = text;
    if (!textChanged) {
      applyLocalPreview(text, enrich);
      return;
    }
    const handle = window.setTimeout(() => applyLocalPreview(text, enrich), 180);
    return () => window.clearTimeout(handle);
  }, [text, enrich, applyLocalPreview]);

  useEffect(() => {
    if (loading || !enrich) {
      requestId.current += 1;
      inflight.current = null;
      onWritingChange?.(false);
      return;
    }

    const trimmed = text.trim();
    if (
      trimmed.length < AI_MIN_CHARS ||
      (trimmed === lastAiText.current && lastEnrichedDraft.current)
    ) {
      onWritingChange?.(false);
      return;
    }

    const handle = window.setTimeout(() => {
      const id = ++requestId.current;
      onWritingChange?.(true);
      const pending = extractDraftFromNotes(
        trimmed,
        { name, email },
        true,
        latestDraft.current,
      ).then((merged) => {
        if (id !== requestId.current) return merged?.draft ?? null;
        if (!merged) return null;
        lastAiText.current = trimmed;
        lastEnrichedDraft.current = merged.draft;
        lastCallId.current = merged.callId;
        publish(merged.draft, false);
        return merged.draft;
      });
      inflight.current = pending;
      inflightText.current = trimmed;
      void pending.finally(() => {
        if (id === requestId.current) onWritingChange?.(false);
      });
    }, AI_DEBOUNCE_MS);

    return () => {
      window.clearTimeout(handle);
      requestId.current += 1;
    };
  }, [text, name, email, enrich, loading, publish, onWritingChange]);

  const locked = loading || submitting;
  const coverage = notesCoverage(draft);
  const hasRole = coverage.role;
  const blankCount = placeholdersIn(text).length;
  const blockingBlanks = blockingPlaceholdersIn(text).length;
  const canSubmit = !locked && text.trim().length >= 12 && blockingBlanks === 0;

  const markStarted = () => {
    if (startedRef.current) return;
    startedRef.current = true;
    onStarted?.();
  };

  const applySelect = (start: number, end: number) => {
    fieldRef.current?.focus();
    fieldRef.current?.selectRange(start, end);
  };

  const insert = (snippet: string, hint: ChipKey) => {
    markStarted();
    const current = text;
    const target = findHintTarget(current, hint);
    if (target) {
      window.requestAnimationFrame(() => applySelect(target.start, target.end));
      return;
    }
    if (current.search(FACT_STEM[hint]) >= 0 || coverage[hint]) {
      window.requestAnimationFrame(() => fieldRef.current?.focus());
      return;
    }

    const next = joinFact(current, snippet).slice(0, MAX_CHARS);
    const insertAt = next.lastIndexOf(snippet);
    const first = insertAt >= 0 ? placeholdersIn(snippet)[0] : undefined;
    pendingSelect.current =
      first && insertAt >= 0
        ? { start: insertAt + first.start, end: insertAt + first.end }
        : { start: next.length, end: next.length };
    setText(next);
  };

  useLayoutEffect(() => {
    const range = pendingSelect.current;
    if (!range) return;
    applySelect(range.start, range.end);
    pendingSelect.current = null;
  }, [text]);

  const status = useMemo(() => {
    if (writing) return takingLonger ? t`Taking longer than usual…` : t`Drafting onto the page…`;
    if (blockingBlanks > 0) return t`Fill the highlighted blanks, or delete them, then continue.`;
    if (!hasRole && text.trim().length > 0) return t`A workplace name is enough to continue.`;
    return null;
  }, [writing, takingLonger, text, hasRole, blockingBlanks]);

  const submit = async () => {
    if (!canSubmit) return;
    trackProductEvent("intake_continue_clicked", withOnboardingTiming({ enrich, has_experience: hasRole }));
    reportAiOutcome(lastCallId.current, "continued");
    setSubmitting(true);
    const trimmed = text.trim();
    try {
      if (!enrich) {
        await onSubmit(trimmed, latestDraft.current);
        return;
      }
      if (trimmed === lastAiText.current && lastEnrichedDraft.current) {
        await onSubmit(trimmed, lastEnrichedDraft.current);
        return;
      }
      const pending: Promise<ResumeDraft | null> =
        inflight.current && inflightText.current === trimmed
          ? inflight.current
          : extractDraftFromNotes(trimmed, { name, email }, true, latestDraft.current).then(
              (merged) => {
                if (!merged) return null;
                lastCallId.current = merged.callId;
                return merged.draft;
              },
            );
      inflight.current = pending;
      inflightText.current = trimmed;
      onWritingChange?.(true);
      const aiDraft = await pending;
      onWritingChange?.(false);
      if (aiDraft && inflightText.current === trimmed) {
        lastAiText.current = trimmed;
        lastEnrichedDraft.current = aiDraft;
        latestDraft.current = aiDraft;
      }
      await onSubmit(trimmed, aiDraft ?? latestDraft.current);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex h-full min-h-0 flex-1 flex-col overflow-hidden px-5 pb-[max(1rem,env(safe-area-inset-bottom))] pt-4 sm:px-7 sm:pt-5 lg:p-6 lg:pb-5">
      <div className="shrink-0 space-y-2">
        <h1 className="text-[1.65rem] font-semibold leading-[1.15] tracking-tight text-foreground sm:text-[1.85rem]">
          {firstName ? t`Hi, ${firstName}.` : t`Hi there.`}
        </h1>
        <div className="max-w-md space-y-1.5 text-[14px] font-medium leading-snug text-foreground/80">
          <p>
            {t`Tell us what you've done. We'll put a first draft on this page, and use placeholders where we still need facts.`}
          </p>
          <p>
            {t`Continue to editor when you're ready. There you can rewrite any line, generate bullets, chat with AI, switch templates, and export a PDF.`}
          </p>
        </div>
      </div>

      <div className="mt-4 flex min-h-0 flex-1 flex-col">
        {status ? (
          <div className="mb-2 flex shrink-0 items-center gap-2 text-[13px] font-medium text-foreground/75">
            <Sparkle
              size={14}
              weight={writing ? "fill" : "duotone"}
              className={
                writing ? "shrink-0 animate-pulse text-primary" : "shrink-0 text-primary/80"
              }
            />
            <span>{status}</span>
          </div>
        ) : null}

        <div className="mb-2.5 shrink-0">
          <PromptHints
            draft={draft}
            onInsert={(snippet, hint) => {
              trackProductEvent("intake_hint_inserted", { hint });
              insert(snippet, hint);
            }}
            onUseExample={() => {
              markStarted();
              trackProductEvent("intake_example_clicked");
              const note = exampleNote(email);
              const first = placeholdersIn(note)[0];
              pendingSelect.current = first
                ? { start: first.start, end: first.end }
                : { start: note.length, end: note.length };
              setText(note);
            }}
          />
        </div>

        <NoteField
          ref={fieldRef}
          value={text}
          maxLength={MAX_CHARS}
          disabled={locked}
          autoFocus
          placeholder={t`Start with your current job, or use a shortcut above.`}
          onChange={(next) => {
            markStarted();
            setText(next);
          }}
          onSubmit={() => void submit()}
        />

        {blankCount > 0 ? (
          <p className="text-foreground/55 mt-1.5 flex items-center gap-1.5 text-[12px] font-medium">
            <kbd className="rounded border border-border bg-secondary px-1.5 py-0.5 text-[10px] font-semibold text-foreground/70">
              Tab
            </kbd>
            <span>{t`next blank. Type to fill it. Select all, then delete, clears the field.`}</span>
          </p>
        ) : null}

        {error && <p className="mt-2 shrink-0 text-sm text-error">{error}</p>}
      </div>

      <div className="mt-3 shrink-0 border-t border-border/70 pt-3">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <EnrichToggle value={enrich} disabled={locked} onChange={onEnrichChange} />
          <Button
            disabled={!canSubmit}
            onClick={() => void submit()}
            className="h-11 gap-2 sm:min-w-[13.5rem]"
          >
            {locked ? (
              <>
                <CircleNotch size={16} className="animate-spin" />
                {t`Opening…`}
              </>
            ) : (
              <>
                {t`Continue to editor`}
                <kbd className="bg-primary-foreground/15 hidden rounded px-1.5 py-0.5 text-[10px] font-medium sm:inline">
                  {isMac ? "⌘" : "Ctrl"}
                  {"⏎"}
                </kbd>
              </>
            )}
          </Button>
        </div>

        <div className="mt-2 flex flex-wrap items-center gap-2">
          {onImport ? (
            <Button type="button" variant="outline" size="sm" disabled={locked} onClick={onImport}>
              {t`Import a file`}
            </Button>
          ) : null}
          {onSkip ? (
            <Button type="button" variant="ghost" size="sm" disabled={locked} onClick={onSkip}>
              {t`Skip, start blank`}
            </Button>
          ) : null}
        </div>
      </div>
    </div>
  );
};
